<script setup>
  import {computed, inject, ref} from "vue";

  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import {router} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";

  import {simpleAttributeText, simpleAttributeTitle} from "./Enums.js";

  defineOptions({
    name: "PatientsPatientAttributesSimpleAttribute",
  });

  const props = defineProps({
    attribute: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    // Passed by parent to highlight newly added attributes (not used in this component, but needed for consistent API)
    recentlyAdded: {
      type: Boolean,
      default: false,
    },
  });

  const broadcast = inject("$broadcast");

  const refConfirmationDialog = ref(null);

  const attributeTitle = computed(() => simpleAttributeTitle[props.attribute.type] ?? null);
  const attributeText = computed(() => simpleAttributeText[props.attribute.type] ?? null);

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    router.delete(route("attributes.destroy", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }
</script>

<template>
  <ComponentCard class="group relative hover:bg-gray-100" :test-id="getTestId('simple-attribute')">
    <div class="flex items-baseline justify-between">
      <h6 class="text-xl">{{ attributeTitle }}</h6>
      <span class="text-xs text-gray-500">Hinzugefügt {{ attribute.updated_at }}</span>
    </div>

    <div class="mt-0.5">{{ attributeText }}</div>

    <div class="absolute right-2 bottom-2 flex">
      <MedButton
        :aria-label="attributeTitle ? `Angabe ${attributeTitle} löschen` : 'Angabe löschen'"
        flat
        icon="delete"
        rounded="full"
        :data-test="getTestId('simple-attribute-card-delete-button')"
        @click="confirmRemove()"
      />
    </div>
  </ComponentCard>

  <ComponentConfirmationDialog
    ref="refConfirmationDialog"
    title="Sind Sie sicher?"
    :content="`Die Angabe „${attributeTitle}“ wird gelöscht.`"
    @confirmed="remove()"
  />
</template>
